
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Briefcase, TrendingUp, Clock, Star } from "lucide-react";
import { useJobList } from "@/hooks/useJobList";
import type { Candidate } from "../types";

interface JobMatchingSectionProps {
  candidate: Candidate;
}

interface JobMatch {
  jobId: string;
  jobTitle: string;
  company: string;
  matchPercentage: number;
  skillsMatch: number;
  experienceMatch: number;
  salaryMatch: number;
  matchingSkills: string[];
  missingSkills: string[];
  salaryRange?: string;
}

export const JobMatchingSection: React.FC<JobMatchingSectionProps> = ({ candidate }) => {
  const { data: jobs = [] } = useJobList();

  const calculateJobMatches = (): JobMatch[] => {
    return jobs.map(job => {
      const candidateSkills = candidate.skills || [];
      const jobSkills = job.skills || [];
      const requiredExperience = job.experience_required || 0;
      const candidateExperience = candidate.experience_years || 0;

      // Skills matching
      const matchingSkills = candidateSkills.filter(skill => 
        jobSkills.some(jobSkill => 
          jobSkill.toLowerCase().includes(skill.toLowerCase()) ||
          skill.toLowerCase().includes(jobSkill.toLowerCase())
        )
      );
      const missingSkills = jobSkills.filter(skill => 
        !candidateSkills.some(candSkill => 
          candSkill.toLowerCase().includes(skill.toLowerCase()) ||
          skill.toLowerCase().includes(candSkill.toLowerCase())
        )
      );
      
      const skillsMatch = jobSkills.length > 0 ? (matchingSkills.length / jobSkills.length) * 100 : 50;

      // Experience matching
      const experienceMatch = requiredExperience > 0 
        ? Math.min((candidateExperience / requiredExperience) * 100, 100)
        : candidateExperience > 0 ? 100 : 50;

      // Salary matching (mock calculation)
      const salaryMatch = 85; // This would be calculated based on actual salary data

      // Overall match
      const matchPercentage = Math.round((skillsMatch * 0.5 + experienceMatch * 0.3 + salaryMatch * 0.2));

      return {
        jobId: job.id,
        jobTitle: job.title,
        company: job.company || 'Unknown Company',
        matchPercentage,
        skillsMatch: Math.round(skillsMatch),
        experienceMatch: Math.round(experienceMatch),
        salaryMatch: Math.round(salaryMatch),
        matchingSkills,
        missingSkills,
        salaryRange: job.salary
      };
    })
    .filter(match => match.matchPercentage > 30)
    .sort((a, b) => b.matchPercentage - a.matchPercentage)
    .slice(0, 5);
  };

  const jobMatches = calculateJobMatches();

  const getMatchColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600 bg-green-100";
    if (percentage >= 60) return "text-yellow-600 bg-yellow-100";
    return "text-orange-600 bg-orange-100";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Job Matching Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {jobMatches.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No suitable job matches found. Consider updating candidate skills or adding more job openings.
          </p>
        ) : (
          jobMatches.map((match) => (
            <div key={match.jobId} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold">{match.jobTitle}</h4>
                  <p className="text-sm text-muted-foreground">{match.company}</p>
                  {match.salaryRange && (
                    <p className="text-sm text-muted-foreground">{match.salaryRange}</p>
                  )}
                </div>
                <Badge className={getMatchColor(match.matchPercentage)}>
                  {match.matchPercentage}% Match
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Skills</span>
                    <span>{match.skillsMatch}%</span>
                  </div>
                  <Progress value={match.skillsMatch} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Experience</span>
                    <span>{match.experienceMatch}%</span>
                  </div>
                  <Progress value={match.experienceMatch} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Salary</span>
                    <span>{match.salaryMatch}%</span>
                  </div>
                  <Progress value={match.salaryMatch} className="h-2" />
                </div>
              </div>

              {match.matchingSkills.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-600 mb-2">Matching Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {match.matchingSkills.map(skill => (
                      <Badge key={skill} variant="secondary" className="text-xs bg-green-100 text-green-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {match.missingSkills.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-orange-600 mb-2">Missing Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {match.missingSkills.slice(0, 5).map(skill => (
                      <Badge key={skill} variant="outline" className="text-xs border-orange-200 text-orange-600">
                        {skill}
                      </Badge>
                    ))}
                    {match.missingSkills.length > 5 && (
                      <span className="text-xs text-muted-foreground">+{match.missingSkills.length - 5} more</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline">
                  <Star className="h-4 w-4 mr-1" />
                  Apply
                </Button>
                <Button size="sm" variant="ghost">
                  View Job Details
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
